import React, { useState, useCallback, useRef } from 'react';
import ColourWheel from './ColourWheel';
import HarmonySection from './HarmonySection';
import SwatchCard from './SwatchCard';
import BlogPage from './BlogPage';
import HomePage from './HomePage';
import MiniBlog from './MiniBlogPage';
import { hexToHsl, getHarmonies, getHueLabel } from './colorUtils';
import { useWindowWidth } from './useWindowWidth';

const HARMONY_HUES = {
  complementary: (h) => [(h + 180) % 360],
  splitComplementary: (h) => [(h + 150) % 360, (h + 210) % 360],
  analogous: (h) => [-60, -30, 0, 30, 60].map(o => (h + o + 360) % 360),
  triadic: (h) => [0, 120, 240].map(o => (h + o) % 360),
  tetradic: (h) => [0, 90, 180, 270].map(o => (h + o) % 360),
};

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'complementary', label: 'Comp' },
  { key: 'splitComplementary', label: 'Split' },
  { key: 'analogous', label: 'Analog' },
  { key: 'triadic', label: 'Triadic' },
  { key: 'tetradic', label: 'Tetrad' },
];

const BRAND = {
  navy: '#1a2a5e',
  navyDark: '#111d42',
  navyDeep: '#0b1228',
  red: '#c41230',
  redMuted: 'rgba(196,18,48,0.15)',
  white: '#ffffff',
  offWhite: '#e8eaf0',
  border: 'rgba(255,255,255,0.1)',
  borderStrong: 'rgba(255,255,255,0.18)',
  panelBg: '#1a2a5e',
  panelBorder: 'rgba(255,255,255,0.09)',
};

export default function App() {
  const [page, setPage] = useState('about');
  const [selectedHex, setSelectedHex] = useState('#c41230');
  const [activeFilter, setActiveFilter] = useState('all');
  const [openSection, setOpenSection] = useState('complementary');
  const [hexInput, setHexInput] = useState('#c41230');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const colorInputRef = useRef(null);
  const width = useWindowWidth();
  const isMobile = width < 768;
  const isTablet = width < 1024;

  const hsl = hexToHsl(selectedHex);
  const harmonies = getHarmonies(selectedHex);

  const highlightedHues = activeFilter === 'all'
    ? null
    : [hsl.h, ...HARMONY_HUES[activeFilter](hsl.h)];

  const handleSelect = useCallback((hex) => {
    setSelectedHex(hex);
    setHexInput(hex);
  }, []);

  const handleHexInput = (e) => {
    const val = e.target.value;
    setHexInput(val);
    if (/^#[0-9A-Fa-f]{6}$/.test(val)) setSelectedHex(val);
  };

  const handleNativeColorChange = (e) => {
    setSelectedHex(e.target.value);
    setHexInput(e.target.value);
  };

  const navigate = (p) => {
    setPage(p);
    setMobileMenuOpen(false);
  };

  const navTabStyle = (active) => ({
    padding: isMobile ? '10px 20px' : '6px 18px',
    background: active ? 'rgba(196,18,48,0.15)' : 'none',
    border: `1px solid ${active ? BRAND.red : 'transparent'}`,
    borderRadius: 3,
    color: active ? BRAND.white : 'rgba(232,234,240,0.5)',
    fontFamily: "'Cinzel', serif",
    fontSize: isMobile ? 13 : 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    outline: 'none',
    width: isMobile ? '100%' : 'auto',
    textAlign: isMobile ? 'left' : 'center',
  });

  const hueName = getHueLabel(hsl.h);

  // Banner height changes on mobile
  const bannerHeight = isMobile ? 80 : 140;
  const navTop = isMobile ? bannerHeight + 3 : 143;
  const navHeight = 42;
  const contentMarginTop = bannerHeight + navHeight + 8;

  return (
    <div style={{
      minHeight: '100vh',
      background: BRAND.navyDeep,
      color: BRAND.offWhite,
      fontFamily: "'Crimson Pro', serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
    }}>
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: `
          linear-gradient(135deg, rgba(26,42,94,0.6) 25%, transparent 25%) -20px 0,
          linear-gradient(225deg, rgba(26,42,94,0.6) 25%, transparent 25%) -20px 0,
          linear-gradient(315deg, rgba(26,42,94,0.6) 25%, transparent 25%),
          linear-gradient(45deg,  rgba(26,42,94,0.6) 25%, transparent 25%)
        `,
        backgroundSize: '80px 80px',
        backgroundRepeat: 'repeat',
        opacity: 0.25,
      }} />

      {/* Banner */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 20,
        borderBottom: `3px solid ${BRAND.red}`,
        background: '#ffffff',
        height: bannerHeight,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'stretch',
      }}>
        {/* Left logo */}
        <div style={{
          position: 'relative',
          width: isMobile ? 80 : 320,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: isMobile ? 10 : 20,
        }}>
          <img src="/logo_banner.png" alt="CyberMod.io"
            style={{ height: '90%', width: 'auto', display: 'block', objectFit: 'contain' }} />
          <div style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: `linear-gradient(to right, transparent 65%, #ffffff 100%)`,
          }} />
        </div>

        {/* Centre: title */}
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', textAlign: 'center',
          padding: '0 8px',
        }}>
          <h1 style={{
            fontFamily: "'Cinzel', serif",
            fontSize: isMobile ? 'clamp(13px, 4vw, 18px)' : 'clamp(18px, 2.8vw, 32px)',
            fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
            margin: 0, color: BRAND.navy, lineHeight: 1.2,
          }}>
            {page === 'palette' ? 'Miniature Palette' : 'Who Are You, Really?'}
          </h1>
          {!isMobile && (
            <p style={{
              fontFamily: "'Crimson Pro', serif", fontStyle: 'italic',
              color: BRAND.red, fontSize: 15, margin: '8px 0 0',
              letterSpacing: '0.04em', lineHeight: 1.4,
            }}>
              {page === 'palette'
                ? 'Colour theory for the painting table · CyberMod.io'
                : 'Identity, access & the odd poorly painted Space Marine · CyberMod.io'}
            </p>
          )}
        </div>

        {/* Right logo — hidden on mobile */}
        {!isMobile && (
          <div style={{
            position: 'relative', width: 320, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 20,
          }}>
            <div style={{
              position: 'absolute', inset: 0, pointerEvents: 'none',
              background: `linear-gradient(to left, transparent 65%, #ffffff 100%)`,
            }} />
            <img src="/logo_banner.png" alt="CyberMod.io"
              style={{ height: '90%', width: 'auto', display: 'block', objectFit: 'contain'}} />
          </div>
        )}

        {/* Mobile hamburger */}
        {isMobile && (
          <button
            onClick={() => setMobileMenuOpen(o => !o)}
            style={{
              flexShrink: 0, width: 52, background: 'none', border: 'none',
              cursor: 'pointer', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 5, padding: '0 12px',
            }}>
            {[0,1,2].map(i => (
              <span key={i} style={{
                display: 'block', width: 22, height: 2,
                background: BRAND.navy, borderRadius: 2,
                transition: 'all 0.2s',
                transform: mobileMenuOpen
                  ? i === 0 ? 'rotate(45deg) translateY(7px)' : i === 2 ? 'rotate(-45deg) translateY(-7px)' : 'scaleX(0)'
                  : 'none',
              }} />
            ))}
          </button>
        )}
      </div>

      {/* Mobile dropdown menu */}
      {isMobile && mobileMenuOpen && (
        <div style={{
          position: 'fixed', top: bannerHeight + 3, left: 0, right: 0, zIndex: 19,
          background: BRAND.navyDark, borderBottom: `1px solid ${BRAND.panelBorder}`,
          display: 'flex', flexDirection: 'column',
        }}>
          {['about', 'cyberblog', 'miniblog', 'palette'].map(p => (
            <button key={p} style={navTabStyle(page === p)} onClick={() => navigate(p)}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
      )}

      {/* Desktop nav bar */}
      {!isMobile && (
        <div style={{
          position: 'fixed', top: navTop, left: 0, right: 0, zIndex: 20,
          background: BRAND.navyDark, borderBottom: `1px solid ${BRAND.panelBorder}`,
          display: 'flex', justifyContent: 'center',
        }}>
          <nav style={{ display: 'flex', gap: 0 }}>
            <button style={navTabStyle(page === 'about')} onClick={() => navigate('about')}>About</button>
            <button style={navTabStyle(page === 'cyberblog')} onClick={() => navigate('cyberblog')}>CyberBlog</button>
            <button style={navTabStyle(page === 'miniblog')} onClick={() => navigate('miniblog')}>MiniBlog</button>
            <button style={navTabStyle(page === 'palette')} onClick={() => navigate('palette')}>Palette</button>
          </nav>
        </div>
      )}

      {/* Blog pages — full viewport width, outside constrained wrapper */}
      {(page === 'cyberblog' || page === 'miniblog') && (
        <div style={{ position: 'relative', zIndex: 1, marginTop: contentMarginTop }}>
          {page === 'cyberblog' && <BlogPage sidebarTop={bannerHeight + navHeight} />}
          {page === 'miniblog' && <MiniBlog sidebarTop={bannerHeight + navHeight} />}
        </div>
      )}

      {/* Content — constrained wrapper for About and Palette */}
      <div style={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 1200,
        padding: isMobile ? '20px 16px 0' : '32px 20px 0',
        boxSizing: 'border-box',
        marginTop: (page === 'cyberblog' || page === 'miniblog') ? 0 : contentMarginTop,
        marginLeft: 'auto',
        marginRight: 'auto',
        display: (page === 'cyberblog' || page === 'miniblog') ? 'none' : 'block',
      }}>
        {page === 'about' && <HomePage onNavigate={navigate} />}

        {page === 'palette' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

            {/* Filter tab bar */}
            <div style={{
              display: 'flex', gap: 0,
              background: BRAND.panelBg,
              border: `1px solid ${BRAND.borderStrong}`,
              borderRadius: 6, overflow: 'hidden', width: '100%',
            }}>
              {FILTER_OPTIONS.map((opt, i) => {
                const isActive = activeFilter === opt.key;
                return (
                  <button key={opt.key}
                    onClick={() => { setActiveFilter(opt.key); if (opt.key !== 'all') setOpenSection(opt.key); else setOpenSection(null); }}
                    style={{
                      flex: 1,
                      padding: isMobile ? '10px 4px' : '11px 8px',
                      background: isActive ? BRAND.red : 'transparent',
                      border: 'none',
                      borderRight: i < FILTER_OPTIONS.length - 1 ? `1px solid ${BRAND.borderStrong}` : 'none',
                      color: isActive ? BRAND.white : 'rgba(232,234,240,0.65)',
                      fontFamily: "'Cinzel', serif",
                      fontSize: isMobile ? 8 : 11,
                      letterSpacing: isMobile ? '0.04em' : '0.12em',
                      textTransform: 'uppercase',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      whiteSpace: 'nowrap',
                    }}>{opt.label}</button>
                );
              })}
            </div>

            {/* Palette columns — stack on mobile */}
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 20 : 40,
              alignItems: 'flex-start',
            }}>

              {/* How to use — hidden on mobile to save space */}
              {!isMobile && (
                <div style={{ width: 220, flexShrink: 0 }}>
                  <div style={{
                    background: BRAND.panelBg, border: `1px solid ${BRAND.panelBorder}`,
                    borderTop: `2px solid ${BRAND.red}`, borderRadius: 6, padding: '18px 16px',
                  }}>
                    <div style={{ fontFamily: "'Cinzel', serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: BRAND.red, marginBottom: 14 }}>
                      How To Use
                    </div>
                    {[
                      { step: '1', text: 'Click anywhere on the colour wheel to sample a hue. The outer ring is lighter, the inner ring darker.' },
                      { step: '2', text: 'Type a hex value directly or click the colour swatch to open your system colour picker.' },
                      { step: '3', text: 'Use the filter tabs above to highlight specific harmony relationships on the wheel.' },
                      { step: '4', text: 'Open a harmony section on the right to see matching swatches and an explanation of when to use it.' },
                      { step: '5', text: 'Click any swatch to copy its hex value to your clipboard.' },
                    ].map(({ step, text }) => (
                      <div key={step} style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
                        <div style={{
                          width: 18, height: 18, borderRadius: '50%',
                          background: BRAND.red, color: BRAND.white,
                          fontFamily: "'Cinzel', serif", fontSize: 9,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, marginTop: 1,
                        }}>{step}</div>
                        <p style={{ fontFamily: "'Crimson Pro', serif", fontSize: 13, color: 'rgba(232,234,240,0.8)', lineHeight: 1.6, margin: 0 }}>{text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Wheel + controls */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, width: isMobile ? '100%' : 'auto' }}>
                <ColourWheel selectedHex={selectedHex} onSelect={handleSelect} highlightedHues={highlightedHues} />

                <div style={{
                  background: BRAND.panelBg, border: `1px solid ${BRAND.borderStrong}`, borderRadius: 6,
                  padding: '14px 18px', width: '100%', maxWidth: 320, boxSizing: 'border-box',
                  display: 'flex', alignItems: 'center', gap: 14,
                }}>
                  <div style={{ position: 'relative', flexShrink: 0 }}>
                    <div onClick={() => colorInputRef.current?.click()} style={{
                      width: 48, height: 48, borderRadius: 4, background: selectedHex, cursor: 'pointer',
                      border: `2px solid ${BRAND.borderStrong}`, boxShadow: `0 0 16px ${selectedHex}66`,
                    }} />
                    <input ref={colorInputRef} type="color" value={selectedHex} onChange={handleNativeColorChange}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "'Cinzel', serif", fontSize: 13, letterSpacing: '0.08em', color: BRAND.white, marginBottom: 4 }}>{hueName}</div>
                    <input type="text" value={hexInput} onChange={handleHexInput} spellCheck={false}
                      style={{
                        background: 'rgba(255,255,255,0.07)', border: `1px solid ${BRAND.borderStrong}`, borderRadius: 3,
                        color: BRAND.offWhite, fontFamily: 'monospace', fontSize: 12, padding: '4px 8px',
                        width: '100%', boxSizing: 'border-box', outline: 'none', letterSpacing: '0.1em',
                      }} placeholder="#rrggbb" />
                    <div style={{ fontSize: 11, color: 'rgba(232,234,240,0.4)', marginTop: 3, fontStyle: 'italic' }}>
                      H:{hsl.h}° S:{hsl.s}% L:{hsl.l}%
                    </div>
                  </div>
                </div>

                <div style={{
                  width: '100%', maxWidth: 320,
                  background: BRAND.panelBg, border: `1px solid ${BRAND.panelBorder}`,
                  borderTop: `2px solid ${BRAND.red}`, borderRadius: 6, padding: '14px 16px', boxSizing: 'border-box',
                }}>
                  <div style={{ fontFamily: "'Cinzel', serif", fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase', color: BRAND.red, marginBottom: 12 }}>Base Tones</div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                    {harmonies.base.swatches.map((sw, i) => <SwatchCard key={i} swatch={sw} size="lg" />)}
                  </div>
                </div>
              </div>

              {/* Harmony panels */}
              <div style={{
                flex: isMobile ? 'none' : '1 1 300px',
                width: isMobile ? '100%' : 'auto',
                minWidth: isMobile ? 0 : 280,
                maxWidth: isMobile ? '100%' : 420,
                background: BRAND.panelBg, border: `1px solid ${BRAND.panelBorder}`,
                borderTop: `2px solid ${BRAND.navy}`, borderRadius: 8,
              }}>
                <div style={{ padding: '14px 20px 12px', borderBottom: `1px solid ${BRAND.panelBorder}`, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 3, height: 16, background: BRAND.red, borderRadius: 2, flexShrink: 0 }} />
                  <div style={{ fontFamily: "'Cinzel', serif", fontSize: 10, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(232,234,240,0.55)' }}>Colour Harmonies</div>
                </div>
                {Object.entries(harmonies).filter(([key]) => key !== 'base').map(([key, harmony]) => (
                  <HarmonySection key={key} harmony={harmony} isActive={openSection === key}
                    onToggle={() => setOpenSection(openSection === key ? null : key)} />
                ))}
              </div>

            </div>
          </div>
        )}

        <footer style={{
          textAlign: 'center', padding: '36px 0 28px',
          color: 'rgba(232,234,240,0.22)', fontSize: 16, fontStyle: 'italic', letterSpacing: '0.05em',
        }}>
          {page === 'palette' ? 'Tap the wheel to sample · Tap any swatch to copy hex' : '2026 Cybermod. Opinions are those of the authors.'}
        </footer>
      </div>

      {/* Bottom nav bar — hidden on palette page */}
      {!isMobile && page !== 'palette' && (
        <div style={{
          width: '100%',
          background: BRAND.navyDark,
          borderTop: `3px solid ${BRAND.red}`,
          display: 'flex', justifyContent: 'center',
          position: 'relative', zIndex: 1,
        }}>
          <nav style={{ display: 'flex', gap: 0 }}>
            <button style={navTabStyle(page === 'about')} onClick={() => navigate('about')}>About</button>
            <button style={navTabStyle(page === 'cyberblog')} onClick={() => navigate('cyberblog')}>CyberBlog</button>
            <button style={navTabStyle(page === 'miniblog')} onClick={() => navigate('miniblog')}>MiniBlog</button>
            <button style={navTabStyle(page === 'palette')} onClick={() => navigate('palette')}>Palette</button>
          </nav>
        </div>
      )}
    </div>
  );
}
